import { rotuloCriticidadeApia, rotuloStatus, type CriticidadeApia as Nivel } from '@qsms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CampoBusca } from '../componentes/CampoBusca.js'
import { ErroApi, api } from '../dados/api.js'
import { banco } from '../dados/banco.js'
import { useArquivo } from '../dados/useArquivo.js'
import { useUsuarioAtual } from '../dados/usuarioAtual.js'
import { PainelAdmin, QuandoFoi, SemRedeAqui } from './admin-comum.js'

/// Revisão da Criticidade Ápia — a tela "Criticidade Apia" do Power Apps.
///
/// Só administrador classifica, como lá. É o segundo olhar sobre o que o inspetor
/// registrou: ele marca a criticidade do achado na hora, em campo; aqui se classifica
/// o risco depois da análise, numa escala própria (Alta, Média, Baixa). Por isso as
/// duas convivem na mesma inspeção em vez de uma sobrescrever a outra.
///
/// O desenho é o do Power Apps: à esquerda a fila, com os filtros; à direita o
/// relatório inteiro da inspeção escolhida, com a classificação em cima. No
/// celular, uma tela de cada vez. Na fila "A revisar", salvar já abre a próxima.
///
/// Trabalha sempre com rede: é revisão de escritório, sobre a base inteira.

const NIVEIS: Nivel[] = ['ALTA', 'MEDIA', 'BAIXA']
const TIPOS = ['Inspeção', 'Notificação', 'Interdição', 'Inspeção Direcionada']
const POR_PAGINA = 50
const CRITICIDADES = ['Leve', 'Moderada', 'Alta']
const EMPRESAS = ['Ápia', 'Fiscalização']

/// Cor de estado de cada nível, sempre com o nome escrito ao lado.
const COR_NIVEL: Record<Nivel, { ativo: string; etiqueta: string }> = {
  ALTA: { ativo: 'border-alta bg-alta text-white', etiqueta: 'bg-alta/10 text-alta' },
  MEDIA: { ativo: 'border-moderada bg-moderada text-white', etiqueta: 'bg-moderada/10 text-moderada' },
  BAIXA: { ativo: 'border-leve bg-leve text-white', etiqueta: 'bg-leve/10 text-leve' },
}

type Fila = 'sem' | Nivel | 'todas'

interface ItemLista {
  id: string
  codigo: string
  titulo: string | null
  status: string
  criticidadeApia: Nivel | null
  criticidadeApiaEm: string | null
  registradoEm: string
  versao: number
  checklist: { nome: string }
  obra: { codigo: string; nome: string }
  criadoPor: { nome: string }
  revisadoPor: { nome: string } | null
  respostas: { campoNome: string; valorTexto: string | null }[]
  _count: { fotos: number; respostas: number }
}

interface RespostaDetalhe {
  id: string
  campoNome: string
  campoTipo: string
  ordem: number
  valorTexto: string | null
  valorTextoLongo: string | null
  valorData: string | null
  valorOpcoes: string[]
  valorBooleano: boolean | null
  arquivoId: string | null
}

interface Detalhe {
  id: string
  codigo: string
  versao: number
  status: string
  titulo: string | null
  registradoEm: string
  assinaturaUsuario: string | null
  criticidadeApia: Nivel | null
  criticidadeApiaEm: string | null
  obra: { codigo: string; nome: string }
  checklist: { nome: string }
  criadoPor: { nome: string }
  revisadoPor: { nome: string } | null
  respostas: RespostaDetalhe[]
  fotos: { id: string; arquivoId: string; descricao: string | null; ordem: number }[]
}

export function CriticidadeApia() {
  const { usuario } = useUsuarioAtual()
  const [fila, setFila] = useState<Fila>('sem')
  const [obraId, setObraId] = useState('')
  const [tipoAcao, setTipoAcao] = useState('')
  const [criticidade, setCriticidade] = useState('')
  const [empresa, setEmpresa] = useState('')
  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)
  const [aberta, setAberta] = useState<string | null>(null)
  // Fica acima do relatório: na fila "A revisar" o relatório já é o da próxima.
  const [ultima, setUltima] = useState<string | null>(null)
  const painel = useRef<HTMLDivElement>(null)
  // No celular a lista some enquanto o relatório está aberto; guarda onde a
  // pessoa estava para devolvê-la ao mesmo ponto quando voltar.
  const rolagemDaLista = useRef<number | null>(null)

  const obras = useLiveQuery(() => banco.obras.orderBy('codigo').toArray(), [], [])

  const params = {
    // O Power Apps lista só o formulário de Inspeção, Notificação e Interdição:
    // os checklists dinâmicos (RA-xxx, PTEs) não passam por esta revisão.
    tipoChecklist: 'INI',
    ...(fila === 'sem' ? { semCriticidadeApia: 'true' } : {}),
    ...(fila !== 'sem' && fila !== 'todas' ? { criticidadeApia: fila } : {}),
    ...(obraId ? { obraId } : {}),
    ...(tipoAcao ? { tipoAcao } : {}),
    ...(criticidade ? { criticidade } : {}),
    ...(empresa ? { empresa } : {}),
    ...(busca ? { busca } : {}),
    resumo: 'true',
    pagina,
  }

  const lista = useQuery({
    queryKey: ['revisao', 'inspecoes', params],
    queryFn: () => api.inspecoes(params),
    placeholderData: (anterior) => anterior,
  })
  const itens = (lista.data?.itens ?? []) as ItemLista[]
  const resumo = (lista.data as { resumo?: Record<string, number> } | undefined)?.resumo ?? {}
  const totalGeral = Object.values(resumo).reduce((s, n) => s + n, 0)
  const total = lista.data?.total ?? 0
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA))

  // Classificar esvazia a fila "A revisar": se a página atual deixou de existir,
  // volta para a última que ainda tem inspeções.
  useEffect(() => {
    if (lista.data && !lista.isPlaceholderData && pagina > paginas) setPagina(paginas)
  }, [lista.data, lista.isPlaceholderData, pagina, paginas])

  // Cada inspeção aberta começa do topo, onde fica a classificação.
  useEffect(() => {
    painel.current?.scrollTo({ top: 0 })
    const celular = !window.matchMedia('(min-width: 1024px)').matches
    if (!celular) return
    if (aberta) {
      window.scrollTo({ top: 0 })
      painel.current?.focus({ preventScroll: true })
    } else if (rolagemDaLista.current !== null) {
      window.scrollTo({ top: rolagemDaLista.current })
      rolagemDaLista.current = null
    }
  }, [aberta])

  function abrir(id: string) {
    if (!aberta) rolagemDaLista.current = window.scrollY
    setUltima(null)
    setAberta(id)
  }

  function mudar(acao: () => void) {
    acao()
    setPagina(1)
    setAberta(null)
    setUltima(null)
    rolagemDaLista.current = null
  }

  const temFiltro = Boolean(obraId || tipoAcao || criticidade || empresa || busca)

  if (usuario && !usuario.admin) {
    return (
      <PainelAdmin titulo="Criticidade Ápia" subtitulo="Classificação do risco depois da análise">
        <p className="cartao p-6 text-center text-sm text-slate-600">Só administradores classificam a Criticidade Ápia.</p>
      </PainelAdmin>
    )
  }

  return (
    <PainelAdmin titulo="Criticidade Ápia" subtitulo="Classificação do risco das inspeções registradas em campo">
      {/* Fila por situação: a que interessa é "A revisar", com o número de cada uma. */}
      <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1" role="group" aria-label="Situação da revisão">
        {(['sem', ...NIVEIS, 'todas'] as Fila[]).map((f) => {
          const n = f === 'todas' ? totalGeral : (resumo[f === 'sem' ? 'SEM' : f] ?? 0)
          const ativo = fila === f
          return (
            <button
              key={f}
              aria-pressed={ativo}
              onClick={() => mudar(() => setFila(f))}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-sm font-semibold transition ${
                ativo ? 'border-marinho bg-marinho text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-marinho'
              }`}
            >
              {f === 'sem' ? 'A revisar' : f === 'todas' ? 'Todas' : rotuloCriticidadeApia[f]}
              {lista.data && <span className="ml-1.5 font-normal opacity-80">{n.toLocaleString('pt-BR')}</span>}
            </button>
          )
        })}
      </div>

      <div className="lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start lg:gap-4">
        {/* Fila. No celular some enquanto o relatório está aberto. */}
        <div className={aberta ? 'hidden lg:block' : ''}>
          <div className="cartao mb-3 grid gap-2 p-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <CampoBusca valor={busca} aoMudar={(v) => mudar(() => setBusca(v))} placeholder="Buscar por código ou local" />
            </div>
            <select value={obraId} onChange={(e) => mudar(() => setObraId(e.target.value))} className="campo" aria-label="Obra">
              <option value="">Todas as obras</option>
              {obras.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.codigo} — {o.nome}
                </option>
              ))}
            </select>
            <Filtro rotulo="Tipo" valor={tipoAcao} opcoes={TIPOS} onMudar={(v) => mudar(() => setTipoAcao(v))} />
            <Filtro rotulo="Criticidade do inspetor" valor={criticidade} opcoes={CRITICIDADES} onMudar={(v) => mudar(() => setCriticidade(v))} />
            <Filtro rotulo="Empresa emitente" valor={empresa} opcoes={EMPRESAS} onMudar={(v) => mudar(() => setEmpresa(v))} />
            {temFiltro && (
              <button
                onClick={() =>
                  mudar(() => {
                    setObraId('')
                    setTipoAcao('')
                    setCriticidade('')
                    setEmpresa('')
                    setBusca('')
                  })
                }
                className="text-left text-sm font-semibold text-marinho underline sm:col-span-2"
              >
                Limpar filtros
              </button>
            )}
          </div>

          {lista.isError && <SemRedeAqui erro={lista.error} />}
          {lista.isLoading && <p className="py-8 text-center text-slate-500">Carregando…</p>}

          {lista.data && (
            <>
              <p className="mb-2 text-sm text-slate-600">
                {total === 0
                  ? fila === 'sem'
                    ? 'Nenhuma inspeção esperando revisão.'
                    : 'Nenhuma inspeção com esses filtros.'
                  : `${total.toLocaleString('pt-BR')} ${total === 1 ? 'inspeção' : 'inspeções'}`}
              </p>
              <ul className={`space-y-2 transition-opacity ${lista.isFetching ? 'opacity-60' : ''}`}>
                {itens.map((i) => (
                  <li key={i.id}>
                    <Cartao inspecao={i} selecionada={aberta === i.id} onAbrir={() => abrir(i.id)} />
                  </li>
                ))}
              </ul>
              {paginas > 1 && (
                <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                  <button onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1} className="botao-neutro !min-h-0 !py-1.5">
                    ← Anteriores
                  </button>
                  <span className="text-slate-500">
                    página {Math.min(pagina, paginas)} de {paginas}
                  </span>
                  <button
                    onClick={() => setPagina(pagina + 1)}
                    disabled={pagina >= paginas}
                    className="botao-neutro !min-h-0 !py-1.5"
                  >
                    Próximas →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Relatório da inspeção escolhida. No desktop acompanha a lista e rola
            sozinho, senão o fim do relatório só apareceria no fim dos 50 cartões. */}
        <div
          ref={painel}
          tabIndex={-1}
          className={`${aberta ? '' : 'hidden lg:block'} outline-none lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)] lg:overflow-y-auto lg:overscroll-contain`}
        >
          {ultima && (
            <p className="mb-3 rounded bg-leve/10 px-3 py-2 text-sm text-leve" role="status">
              {ultima}
            </p>
          )}
          {aberta ? (
            <Relatorio
              key={aberta}
              id={aberta}
              onVoltar={() => setAberta(null)}
              onSalvo={(texto) => {
                setUltima(texto)
                // Na fila "A revisar", a classificada sai da lista: abre a próxima.
                if (fila !== 'sem') return
                const salva = aberta
                const k = itens.findIndex((i) => i.id === salva)
                const proxima = itens[k + 1] ?? itens[k - 1]
                // A resposta pode chegar depois de a pessoa ter ido para outra
                // inspeção, aba ou filtro: aí não mexe no que ela escolheu.
                setAberta((atual) => (atual !== salva ? atual : proxima && proxima.id !== salva ? proxima.id : null))
              }}
            />
          ) : (
            <p className="cartao p-8 text-center text-sm text-slate-500">
              Escolha uma inspeção na lista para ver o relatório e classificar a Criticidade Ápia.
            </p>
          )}
        </div>
      </div>
    </PainelAdmin>
  )
}

function Filtro({
  rotulo,
  valor,
  opcoes,
  onMudar,
}: {
  rotulo: string
  valor: string
  opcoes: string[]
  onMudar: (v: string) => void
}) {
  return (
    <select value={valor} onChange={(e) => onMudar(e.target.value)} className="campo" aria-label={rotulo}>
      <option value="">{rotulo}: todos</option>
      {opcoes.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  )
}

const resposta = (i: ItemLista, campo: string) => i.respostas.find((r) => r.campoNome === campo)?.valorTexto ?? null

function Cartao({ inspecao, selecionada, onAbrir }: { inspecao: ItemLista; selecionada: boolean; onAbrir: () => void }) {
  const tipo = resposta(inspecao, 'Tipo Ação')
  const criticidade = resposta(inspecao, 'Criticidade')
  const tipologia = resposta(inspecao, 'Tipologia')
  return (
    <button
      onClick={onAbrir}
      aria-current={selecionada}
      className={`cartao block w-full p-3 text-left transition hover:border-marinho ${selecionada ? 'border-marinho ring-2 ring-marinho/20' : ''}`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs text-slate-500">{inspecao.codigo}</p>
          <p className="truncate font-semibold text-slate-900">
            {inspecao.titulo || resposta(inspecao, 'Titulo') || inspecao.checklist.nome}
          </p>
          <p className="truncate text-xs text-slate-500">
            {inspecao.obra.codigo} · {inspecao.obra.nome} · {inspecao.criadoPor.nome}
          </p>
        </div>
        <div className="shrink-0 text-right">
          {inspecao.criticidadeApia ? (
            <span className={`etiqueta ${COR_NIVEL[inspecao.criticidadeApia].etiqueta}`}>
              {rotuloCriticidadeApia[inspecao.criticidadeApia]}
            </span>
          ) : (
            <span className="etiqueta bg-slate-100 text-slate-500">a revisar</span>
          )}
          <p className="mt-1 text-xs text-slate-500">
            <QuandoFoi iso={inspecao.registradoEm} />
          </p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
        {tipo && <span className="etiqueta bg-marinho-50 text-marinho">{tipo}</span>}
        {criticidade && <span className="etiqueta bg-slate-100 text-slate-600">inspetor: {criticidade}</span>}
        {tipologia && <span className="truncate text-slate-500">{tipologia}</span>}
      </div>
    </button>
  )
}

/// Os nomes dos campos do INI como aparecem no relatório do Power Apps.
const ROTULO: Record<string, string> = {
  'Tipo Ação': 'Tipo ação',
  'Data Criacao': 'Data de criação',
  Titulo: 'Frente de serviço / localização',
  'Empresa Emitente': 'Empresa emitente',
  'Tecnico Seguranca': 'Técnico de segurança',
  'Engenheiro Seguranca': 'Engenheiro de segurança',
  'Gerente Contrato': 'Gerente de contrato',
  'Descricao Acao': 'Descrição',
  'Descricao Plano Acao': 'Plano de ação',
  'Data Prevista Plano Acao': 'Data prevista',
  'Data Efetiva Plano Acao': 'Data efetiva',
  'Assinatura Usuario': 'Responsável (digitado)',
}

function Relatorio({ id, onVoltar, onSalvo }: { id: string; onVoltar: () => void; onSalvo: (texto: string) => void }) {
  const cliente = useQueryClient()
  const detalhe = useQuery({
    queryKey: ['revisao', 'inspecao', id],
    queryFn: () => api.inspecao(id) as Promise<Detalhe>,
  })
  const d = detalhe.data
  const [escolha, setEscolha] = useState<Nivel | null>(null)
  useEffect(() => setEscolha(d?.criticidadeApia ?? null), [d?.criticidadeApia])

  const salvar = useMutation({
    mutationFn: (nivel: Nivel) => api.revisarCriticidade(id, nivel, d!.versao),
    onSuccess: (r, nivel) => {
      // Grava já a versão nova: até o recarregamento chegar, um segundo
      // salvamento iria com a antiga e levaria um 409 pelo próprio clique.
      cliente.setQueryData<Detalhe>(['revisao', 'inspecao', id], (a) =>
        a && { ...a, versao: r.versao, criticidadeApia: nivel, criticidadeApiaEm: new Date().toISOString() },
      )
      void cliente.invalidateQueries({ queryKey: ['revisao'] })
      onSalvo(`${d!.codigo} classificada como ${rotuloCriticidadeApia[nivel]}.`)
    },
    onError: (e) => {
      // Alguém mexeu na inspeção enquanto ela estava aberta: recarrega o
      // relatório e a fila para a pessoa ver o que mudou antes de salvar de novo.
      if (e instanceof ErroApi && e.status === 409) void cliente.invalidateQueries({ queryKey: ['revisao'] })
    },
  })

  if (detalhe.isError) return <SemRedeAqui erro={detalhe.error} />
  if (!d) return <p className="cartao p-8 text-center text-slate-500">Carregando o relatório…</p>

  const respostas = [...d.respostas].sort((a, b) => a.ordem - b.ordem)
  const mudou = escolha !== null && escolha !== d.criticidadeApia

  return (
    <section className="space-y-3">
      <button onClick={onVoltar} className="text-sm font-medium text-marinho lg:hidden">
        ← Voltar à lista
      </button>

      {/* A classificação vem primeiro: é o que se veio fazer aqui. */}
      <div className="cartao border-marinho/30 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Criticidade Ápia</p>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {NIVEIS.map((n) => (
            <button
              key={n}
              onClick={() => setEscolha(n)}
              aria-pressed={escolha === n}
              disabled={salvar.isPending}
              className={`botao border ${escolha === n ? COR_NIVEL[n].ativo : 'border-slate-300 bg-white text-slate-700 hover:border-marinho'}`}
            >
              {rotuloCriticidadeApia[n]}
            </button>
          ))}
        </div>
        <button
          onClick={() => escolha && salvar.mutate(escolha)}
          disabled={!mudou || salvar.isPending}
          className="botao-primario mt-3 w-full"
        >
          {salvar.isPending ? 'Salvando…' : 'Salvar Criticidade Ápia'}
        </button>
        <p className="mt-2 text-xs text-slate-500">
          {d.criticidadeApia ? (
            <>
              Atual: <strong>{rotuloCriticidadeApia[d.criticidadeApia]}</strong>
              {d.revisadoPor && `, por ${d.revisadoPor.nome}`}
              {d.criticidadeApiaEm && (
                <>
                  {' '}
                  <QuandoFoi iso={d.criticidadeApiaEm} />
                </>
              )}
              {/* As que vieram do Power Apps trazem o nível, mas não quem nem quando. */}
              {!d.revisadoPor && !d.criticidadeApiaEm && ' (classificada no Power Apps)'}.
            </>
          ) : (
            'Ainda não classificada.'
          )}
        </p>
        {salvar.isError && (
          <p className="mt-2 text-sm text-alta" role="alert">
            {(salvar.error as Error).message}
          </p>
        )}
      </div>

      <div className="cartao p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-mono text-xs text-slate-500">{d.codigo}</p>
            <h3 className="font-semibold text-slate-900">Relatório da inspeção</h3>
            <p className="text-xs text-slate-500">
              {d.obra.codigo} · {d.obra.nome} · registrada por {d.criadoPor.nome} em{' '}
              {new Date(d.registradoEm).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          </div>
          <span className="etiqueta bg-slate-100 text-slate-600">
            {rotuloStatus[d.status as keyof typeof rotuloStatus] ?? d.status}
          </span>
        </div>

        <dl className="mt-3 grid gap-x-4 gap-y-2 sm:grid-cols-2">
          {respostas.map((r) => (
            <div key={r.id} className={largo(r) ? 'sm:col-span-2' : ''}>
              <dt className="text-xs text-slate-500">{ROTULO[r.campoNome] ?? r.campoNome}</dt>
              <dd className="text-sm text-slate-800">
                <Valor r={r} />
              </dd>
            </div>
          ))}
        </dl>

        {d.fotos.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-slate-500">Fotos ({d.fotos.length})</p>
            <div className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {[...d.fotos]
                .sort((a, b) => a.ordem - b.ordem)
                .map((f) => (
                  <Imagem key={f.id} arquivoId={f.arquivoId} alt={f.descricao ?? 'Foto da inspeção'} />
                ))}
            </div>
          </div>
        )}
        {d.assinaturaUsuario && <p className="mt-3 text-xs text-slate-500">Assinado por {d.assinaturaUsuario}</p>}

        <Link to={`/inspecao/${d.id}/ver`} className="mt-3 inline-block text-sm font-medium text-marinho underline">
          Abrir a inspeção →
        </Link>
      </div>
    </section>
  )
}

function largo(r: RespostaDetalhe): boolean {
  if (r.campoTipo === 'IMAGEM' || r.campoTipo === 'ASSINATURA') return true
  const nome = r.campoNome.toLowerCase()
  return nome.includes('descri') || nome.includes('plano')
}

function Valor({ r }: { r: RespostaDetalhe }) {
  if (r.campoTipo === 'IMAGEM' || r.campoTipo === 'ASSINATURA') {
    return r.arquivoId ? (
      <span className="mt-1 block max-w-xs">
        <Imagem arquivoId={r.arquivoId} alt={r.campoNome} inteira />
      </span>
    ) : r.valorTexto ? (
      <>{r.valorTexto}</>
    ) : (
      <span className="text-slate-400">—</span>
    )
  }
  if (r.valorData) return <>{new Date(r.valorData).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</>
  if (r.valorOpcoes.length > 0) return <>{r.valorOpcoes.join(', ')}</>
  if (r.valorBooleano !== null) return <>{r.valorBooleano ? 'Sim' : 'Não'}</>
  const texto = r.valorTextoLongo ?? r.valorTexto
  return texto ? <span className="whitespace-pre-wrap">{texto}</span> : <span className="text-slate-400">—</span>
}

/// Miniatura quadrada nas fotos; `inteira` para a resposta de imagem ou assinatura,
/// que não pode sair cortada — uma assinatura é larga e baixa.
function Imagem({ arquivoId, alt, inteira }: { arquivoId: string; alt: string; inteira?: boolean }) {
  const { estado, url } = useArquivo(arquivoId)
  if (estado === 'carregando') {
    return <span className={`block animate-pulse rounded bg-slate-100 ${inteira ? 'h-32' : 'aspect-square'}`} />
  }
  if (!url) return <span className="block text-xs text-slate-400">Imagem indisponível</span>
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img
        src={url}
        alt={alt}
        loading="lazy"
        className={`w-full rounded border border-slate-200 ${inteira ? 'max-h-64 bg-white object-contain' : 'aspect-square object-cover'}`}
      />
    </a>
  )
}
